import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/Card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UserOption {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ComposeMessage() {
  const queryClient = useQueryClient();
  const [toUserId, setToUserId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [requestReply, setRequestReply] = useState(false);
  const [confirmReceipt, setConfirmReceipt] = useState(false);
  const [sent, setSent] = useState(false);

  const { data: usersData } = useQuery<{ data: UserOption[] }>({
    queryKey: ["admin", "users"],
    queryFn: () => api.get<{ data: UserOption[] }>("/admin/users"),
  });

  const users = usersData?.data ?? [];

  const userOptions = users.map((u) => ({
    value: String(u.id),
    label: `${u.first_name} ${u.last_name} (${u.email})`,
  }));

  const sendMutation = useMutation({
    mutationFn: (data: {
      toUserId: number;
      subject?: string;
      body: string;
      requestReply?: boolean;
      confirmReceipt?: boolean;
    }) => api.post("/messaging", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messaging"] });
      setSent(true);
      setToUserId("");
      setSubject("");
      setBody("");
      setRequestReply(false);
      setConfirmReceipt(false);
      setTimeout(() => setSent(false), 3000);
    },
  });

  function handleSend() {
    if (!toUserId || !body.trim()) return;
    sendMutation.mutate({
      toUserId: parseInt(toUserId, 10),
      subject: subject || undefined,
      body,
      requestReply: requestReply || undefined,
      confirmReceipt: confirmReceipt || undefined,
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Compose Message</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {sent && (
          <div className="rounded-md bg-green-50 p-3 text-sm text-green-700">
            Message sent successfully.
          </div>
        )}

        <Select
          label="To"
          required
          options={userOptions}
          value={toUserId}
          onChange={setToUserId}
          searchable
          placeholder="Select recipient..."
        />

        <Input
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Optional subject..."
        />

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Message <span className="text-destructive">*</span>
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            placeholder="Type your message..."
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={requestReply}
              onChange={(e) => setRequestReply(e.target.checked)}
              className="rounded border-input"
            />
            Request Reply
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={confirmReceipt}
              onChange={(e) => setConfirmReceipt(e.target.checked)}
              className="rounded border-input"
            />
            Confirm Receipt
          </label>
        </div>

        <div className="flex gap-3">
          <Button
            onClick={handleSend}
            disabled={!toUserId || !body.trim()}
            loading={sendMutation.isPending}
          >
            <Send className="mr-2 h-4 w-4" />
            Send Message
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
